<div class="flex flex-col bg-surface-50 dark:bg-surface-950 min-h-full">
    <div class="flex flex-col gap-3 p-3">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <!-- Document Types Section -->
            <div class="lg:col-span-1 shadow rounded-base p-3 border flex flex-col gap-3">
                <div class="flex flex-col gap-2 h-full max-h-[calc(100vh-7rem)] overflow-hidden">
                    <div class="flex items-center justify-between">
                        <div class="text-surface-900 dark:text-surface-0 font-semibold text-lg">Document Types</div>
                    </div>

                    <div class="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
                        @if(docTypeLoading){
                        @for (item of getSkeletonCount(); track $index) {
                        <div class="w-full h-20 pt-2">
                            <p-skeleton width="100%" height="90%" />
                        </div>
                        }
                        }
                        @else{
                        @for (keyPoint of documentTypes; track keyPoint._id) {
                        <div class="flex flex-row gap-2 border p-3 my-2 justify-between rounded-base items-center cursor-pointer max-w-full"
                            [class.active-key-point]="selectedDocType === keyPoint._id"
                            (click)="changeDocumentType(keyPoint._id)">
                            <div class="flex flex-row gap-2 flex-1 items-center overflow-hidden">
                                <span
                                    class="border p-1.5 sm:p-2 rounded-base key-point-icon inline-flex items-center justify-center text-sm sm:text-base">
                                    <i class="pi pi-file"></i>
                                </span>
                                <div class="min-w-0">
                                    <div class="font-bold truncate text-sm sm:text-base">{{keyPoint.name}}</div>
                                    <div class="text-gray-500 truncate text-xs sm:text-sm"
                                        [pTooltip]="keyPoint.description">
                                        {{keyPoint.description}}
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                        }
                    </div>
                </div>
            </div>

            <!-- Risks Section -->
            <div class="lg:col-span-3 flex flex-col gap-3">
                <div class="flex items-center justify-between gap-3">
                    <div class="text-surface-900 dark:text-surface-0 font-semibold text-lg lg:hidden">Risks</div>
                    <div class="flex justify-end w-full gap-2">
                        <app-button variant="filled" severity="primary" label="Access Risk Matrix" icon="pi pi-plus"
                            (clicked)="onShowDialog()" />
                    </div>
                </div>
                <div class="flex-1 overflow-y-auto scrollbar-thin max-h-[calc(100vh-13rem)]">
                    @if(loading){
                    <div class="skeleton-card">
                        <div #skeletonContainer class="border-round border-1 surface-border surface-card p-3">
                            <div class="flex flex-col items-center gap-4">
                                @for (item of getSkeletonCount(); track $index) {
                                <div class="w-3/5">
                                    <p-skeleton width="100%" height="15rem" />
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                    }
                    @else {
                    @if(!checklists.length) {
                    <p class="text-center">No Risks found</p>
                    }
                    @else {
                    <div class="flex flex-col items-center justify-center gap-3 my-2">
                        @for (checklist of checklists; track checklist._id) {
                        <div class="w-full border flex flex-col p-3 rounded-base shadow-md gap-4">
                            <div class="flex flex-col gap-1 sm:gap-0">
                                <span class="font-bold text-sm sm:text-md">{{ checklist.name }}</span>
                                <span class="text-xs sm:text-sm text-gray-500">{{ checklist.description }}</span>
                            </div>
                            <ng-container *ngTemplateOutlet="checklist._id && impactByChecklist[checklist._id].impactRules?.length ? withRulesTemplate : noRulesTemplate; 
                                        context: { checklist: checklist }">
                            </ng-container>
                            <div class="flex flex-col sm:flex-row gap-2 justify-between">
                                <div class="flex flex-col">
                                    <span class="font-bold text-sm sm:text-base">Probability</span>
                                </div>
                                <div class="flex flex-row gap-2 flex-wrap">
                                    @for(item of defaultProbability; track item; let i = $index) {
                                    <div class="flex flex-col">
                                        <span
                                            class="inline-flex items-center justify-center rounded-base p-2  text-white"
                                            [style]="{backgroundColor: IMPACT_COLOR[i+1], width: '2rem', height: '2rem'}">
                                            {{item}}
                                        </span>
                                        @if(checklist._id) {
                                        <p-radioButton [inputId]="'probability_' + checklist._id + '_' + i"
                                            [name]="'probability_' + checklist._id" [value]="i + 1"
                                            class="risk-radio-button mt-2"
                                            [(ngModel)]="impactByChecklist[checklist._id].probability"
                                            (ngModelChange)="updateProbability(checklist._id)" [disabled]="false">
                                        </p-radioButton>
                                        }
                                    </div>
                                    }
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }
                    }
                </div>
                <div class="flex-shrink-0">
                    <p-paginator (onPageChange)="onPageChange($event)" [first]="first" [rows]="rows"
                        [totalRecords]="totalRecords" [rowsPerPageOptions]="[5, 10, 20]" />
                </div>
            </div>
        </div>
    </div>
</div>

<ng-template #withRulesTemplate let-checklist="checklist">
    <!-- Content for when rules exist -->
    <div class="flex flex-col gap-2">
        <div class="flex flex-col">
            <span class="font-bold text-sm sm:text-base">Impact</span>
            <div class="flex gap-x-4 text-xs sm:text-sm">
                <span class="underline text-arena-orange cursor-pointer" (click)="editRules(checklist._id)">Edit
                    Rules</span>
                <span class="underline text-arena-orange cursor-pointer" (click)="removeRules(checklist._id)">Remove
                    Rules</span>
            </div>
        </div>
        <div class="flex flex-col gap-2">
            @for(rule of (impactByChecklist[checklist._id].impactRules || []); track rule; let i = $index) {
            <div class="flex items-center">
                <p-radioButton [inputId]="'probability_' + checklist._id + '_' + i"
                    [name]="'probability_' + checklist._id" [value]="i + 1" class="risk-radio-button mr-2"
                    [(ngModel)]="impactByChecklist[checklist._id].impact" (ngModelChange)="updateImpact(checklist._id)"
                    [disabled]="false">
                </p-radioButton>
                <span class="inline-flex items-center justify-center rounded-base p-2 text-white"
                    [style]="{backgroundColor: IMPACT_COLOR[i+1], width: '2rem', height: '2rem'}">
                    {{i+1}}
                </span>
                <span class="ml-2 text-xs sm:text-sm">{{rule.rule}}</span>
            </div>
            }
        </div>
    </div>
</ng-template>

<ng-template #noRulesTemplate let-checklist="checklist">
    <div class="flex flex-col sm:flex-row gap-2 justify-between">
        <div class="flex flex-col">
            <span class="font-bold text-sm sm:text-base">Impact</span>
            <span class="underline text-arena-orange cursor-pointer text-xs sm:text-sm"
                (click)="addRules(checklist._id)">Add Rules</span>
        </div>
        <div class="flex flex-row gap-2 flex-wrap">
            @for(rule of defaultImpact; track rule; let i = $index) {
            <div class="flex flex-col">
                <span class="inline-flex items-center justify-center rounded-base p-2 text-white"
                    [style]="{backgroundColor: IMPACT_COLOR[i+1], width: '2rem', height: '2rem'}">
                    {{rule}}
                </span>
                <p-radioButton [inputId]="'probability_' + checklist._id + '_' + i"
                    [name]="'probability_' + checklist._id" [value]="i + 1" class="risk-radio-button mt-2"
                    [(ngModel)]="impactByChecklist[checklist._id].impact" (ngModelChange)="updateImpact(checklist._id)"
                    [disabled]="false">
                </p-radioButton>
            </div>
            }
        </div>
    </div>
</ng-template>

<app-dialog [(visible)]="rulesDialog.visible" title="Add Rules" (closed)="rulesDialog.visible = false"
    class="max-w-[600px]" [fullscreenOnMobile]="true">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-2 p-4">
            @for(rule of rulesDialog.rules; track rule; let i = $index) {
            <div class="flex flex-row gap-2 items-center">
                <span class="inline-flex items-center justify-center rounded-base p-2 text-white flex-shrink-0"
                    [style]="{backgroundColor: IMPACT_COLOR[i+1], width: '2rem', height: '2rem'}">
                    {{i+1}}
                </span>
                <textarea pTextarea [value]="rulesDialog.rules[$index]" (change)="updateRule($index, $event)"
                    [autoResize]="true" class="w-full rounded-base" rows="1"></textarea>
            </div>
            }
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button label="Save" icon="pi pi-save" variant="filled" severity="primary" (clicked)="saveRules()"
                class="w-full sm:w-auto" />
        </div>
    </ng-template>
</app-dialog>

<app-dialog [(visible)]="riskMatrixDialog" title="Risk Matrix" (closed)="riskMatrixDialog = false" class="max-w-[72vw]"
    [fullscreenOnMobile]="true">
    <ng-template #bodyTemplate>
        <app-risk-matrix></app-risk-matrix>
    </ng-template>
</app-dialog>
